import React from "react";
import { LibrarySort } from "@growth-machine-llc/stridist-api-client";
import { AllOption, LibraryPrivacyFilter } from "../constants";

interface BaseConfig {
  defaultQuery?: string;
  defaultSort?: LibrarySort;
  resetOn: React.DependencyList[number];
  defaultPage?: number;
  defaultRowsPerPage?: number;
  defaultPrivacy?: LibraryPrivacyFilter;
}

const DEFAULT_ROWS_PER_PAGE = 10;
const DEFAULT_PAGE = 1;
const DEFAULT_QUERY = "";
const DEFAULT_PRIVACY: LibraryPrivacyFilter = AllOption;
const DEFAULT_SORT = null;

export interface BaseLibraryFilters {
  query: string;
  sortKey: LibrarySort;
  page: number;
  rowsPerPage: number;
  privacy: LibraryPrivacyFilter;
}

export const DEFAULT_BASE_FILTERS: BaseLibraryFilters = {
  query: DEFAULT_QUERY,
  sortKey: DEFAULT_SORT,
  page: DEFAULT_PAGE,
  rowsPerPage: DEFAULT_ROWS_PER_PAGE,
  privacy: DEFAULT_PRIVACY,
};

type BaseState = Pick<
  BaseLibraryFilters,
  "query" | "sortKey" | "page" | "rowsPerPage" | "privacy"
>;

function makeBaseState(cfg: BaseConfig): BaseState {
  return {
    query: cfg.defaultQuery ?? DEFAULT_QUERY,
    sortKey: cfg.defaultSort ?? DEFAULT_SORT,
    privacy: cfg.defaultPrivacy ?? DEFAULT_PRIVACY,
    page: cfg.defaultPage ?? DEFAULT_PAGE,
    rowsPerPage: cfg.defaultRowsPerPage ?? DEFAULT_ROWS_PER_PAGE,
  };
}

export function useBaseLibraryFilters(config: BaseConfig) {
  const [state, setState] = React.useState<BaseState>(() =>
    makeBaseState(config),
  );

  React.useEffect(() => {
    setState(makeBaseState(config));
  }, [
    config.defaultQuery,
    config.defaultSort,
    config.defaultPage,
    config.defaultRowsPerPage,
    config.defaultPrivacy,
    config.resetOn,
  ]);

  const set = React.useCallback(
    <K extends keyof BaseState>(key: K, value: BaseState[K]) => {
      setState((s) => ({ ...s, [key]: value }));
    },
    [],
  );

  const resetBaseFilters = React.useCallback(() => {
    setState(makeBaseState(config));
  }, [
    config.defaultQuery,
    config.defaultSort,
    config.defaultPage,
    config.defaultRowsPerPage,
    config.defaultPrivacy,
  ]);

  const baseFiltersChanged =
    state.query !== (config.defaultQuery ?? DEFAULT_QUERY) ||
    state.sortKey !== (config.defaultSort ?? DEFAULT_SORT) ||
    state.page !== (config.defaultPage ?? DEFAULT_PAGE) ||
    state.rowsPerPage !==
      (config.defaultRowsPerPage ?? DEFAULT_ROWS_PER_PAGE) ||
    state.privacy !== (config.defaultPrivacy ?? DEFAULT_PRIVACY);

  return {
    query: state.query,
    sortKey: state.sortKey,
    page: state.page,
    rowsPerPage: state.rowsPerPage,
    privacy: state.privacy,

    handleQueryChange: (v: string) => set("query", v),
    handleSortKeyChange: (v: LibrarySort) => set("sortKey", v),
    handlePageChange: (v: number) => set("page", v),
    handleRowsPerPageChange: (v: number) => {
      setState((s) => ({
        ...s,
        rowsPerPage: v,
        page: config.defaultPage ?? DEFAULT_PAGE,
      }));
    },
    handleChangePrivacy: (v: LibraryPrivacyFilter) => {
      setState((s) => ({
        ...s,
        privacy: v,
        page: config.defaultPage ?? DEFAULT_PAGE,
      }));
    },

    resetBaseFilters,
    baseFiltersChanged,
  };
}

export function useResettableFilters<T extends object>(
  defaultFilters: T,
  resetOn: React.DependencyList[number],
) {
  const [filters, setFilters] = React.useState<T>(defaultFilters);

  React.useEffect(() => {
    setFilters(defaultFilters);
  }, [resetOn]);

  const updateFilters = React.useCallback((newFilters: Partial<T>) => {
    setFilters((prev) => ({
      ...prev,
      ...newFilters,
    }));
  }, []);

  const resetFilters = React.useCallback(() => {
    setFilters(defaultFilters);
  }, [defaultFilters]);

  const filtersChanged = !areShallowEqual(filters, defaultFilters);

  return {
    filters,
    updateFilters,
    resetFilters,
    filtersChanged,
  };
}

export interface LibraryFiltersStateConfig<
  TFilters extends BaseLibraryFilters,
> {
  defaultFilters?: Partial<TFilters>;
  resetOn: React.DependencyList[number];
}

export function useLibraryFiltersState<TFilters extends BaseLibraryFilters>(
  config: LibraryFiltersStateConfig<TFilters>,
) {
  const { defaultFilters: customDefaults, resetOn } = config;

  const defaultFilters = React.useMemo(
    () =>
      ({
        ...DEFAULT_BASE_FILTERS,
        ...customDefaults,
      }) as TFilters,
    [customDefaults],
  );

  const {
    query,
    sortKey,
    page,
    rowsPerPage,
    privacy,
    handleQueryChange,
    handleSortKeyChange,
    handlePageChange,
    handleRowsPerPageChange,
    handleChangePrivacy,
    resetBaseFilters,
    baseFiltersChanged,
  } = useBaseLibraryFilters({
    defaultQuery: defaultFilters.query,
    defaultSort: defaultFilters.sortKey,
    resetOn,
    defaultPage: defaultFilters.page,
    defaultRowsPerPage: defaultFilters.rowsPerPage,
    defaultPrivacy: defaultFilters.privacy,
  });

  const { filters, updateFilters, resetFilters, filtersChanged } =
    useResettableFilters<TFilters>((customDefaults ?? {}) as TFilters, resetOn);

  const handleResetFilters = React.useCallback(() => {
    resetFilters();
    resetBaseFilters();
  }, [resetFilters, resetBaseFilters]);

  const filtersToReset = baseFiltersChanged || filtersChanged;

  return {
    query,
    sortKey,
    filters,
    page,
    rowsPerPage,
    privacy,
    handleQueryChange,
    handleSortKeyChange,
    updateFilters,
    handlePageChange,
    handleRowsPerPageChange,
    handleChangePrivacy,
    handleResetFilters,
    filtersToReset,
  };
}

function areShallowEqual<T extends object>(a: T, b: T) {
  const keysA = Object.keys(a);
  const keysB = Object.keys(b);
  if (keysA.length !== keysB.length) return false;

  for (const key of keysA) {
    if (a[key] !== b[key]) {
      return false;
    }
  }
  return true;
}
